// Registro central de íconos ec-icon.
// Este archivo es HAND-WRITTEN — a diferencia de linear.ts/solid.ts, NO se
// regenera con `npm run generate:icons`. El script solo toca linear.ts/solid.ts;
// ALIASES y LEGACY_ICONS viven acá para sobrevivir a cada regeneración.
//
// `IconName` se deriva con `keyof typeof` sobre los objetos generados: al
// regenerar linear.ts/solid.ts con íconos nuevos, el tipo se actualiza solo,
// sin tocar este archivo.

import { LINEAR_ICONS } from './linear';
import { SOLID_ICONS } from './solid';

export type IconVariant = 'linear' | 'solid';

/** Unión de los nombres (kebab-case, tal cual Figma) disponibles en cualquiera de las dos variantes. */
export type IconName = keyof typeof LINEAR_ICONS | keyof typeof SOLID_ICONS;

export const ICON_REGISTRY: Record<IconVariant, Record<string, string>> = {
  linear: LINEAR_ICONS,
  solid: SOLID_ICONS
};

/**
 * Alias semánticos y de typos de la librería Figma. Los nombres de Figma se
 * respetan tal cual en el registro (incluidos los typos `delte`, `commnet`,
 * `localtion2`) — estos alias permiten resolverlos por su nombre semántico
 * correcto. Reportar los typos a diseño está fuera de alcance del código.
 *
 * OJO: `comment`/`location2` son casos particulares — el ícono `solid` YA
 * viene bien escrito en Figma (`comment.svg`, `location2.svg`) mientras que
 * el `linear` tiene el typo (`commnet.svg`, `localtion2.svg`). El alias solo
 * hace falta para la variante con typo; getIcon() prioriza el match literal
 * en la variante pedida antes de aplicar el alias, así que la variante ya
 * bien escrita nunca pasa por acá.
 */
export const ALIASES: Record<string, string> = {
  delete: 'delte',
  trash: 'delte',
  comment: 'commnet',
  location2: 'localtion2',
  close: 'dismiss',
  clear: 'dismiss',
  eye: 'show',
  'eye-off': 'hide',
  mail: 'email',
  phone: 'call',
  prev: 'caret-left',
  next: 'caret-right'
};

/**
 * Íconos sin equivalente en la librería Figma (auditoría 2026-07). Se
 * conservan con su SVG Material actual hasta que diseño los entregue.
 * LEGACY: pendiente en librería Figma.
 */
export const LEGACY_ICONS: Record<string, string> = {
  bell: '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 22c1.1 0 2-.9 2-2h-4c0 1.1.89 2 2 2zm6-6v-5c0-3.07-1.64-5.64-4.5-6.32V4c0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5 1.5v.68C7.63 5.36 6 7.92 6 11v5l-2 2v1h16v-1l-2-2z"/></svg>',
  download: '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M19 9h-4V3H9v6H5l7 7zM5 18v2h14v-2z"/></svg>',
  filter: '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M10 18h4v-2h-4v2zM3 6v2h18V6H3zm3 7h12v-2H6v2z"/></svg>',
  lock: '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9a2 2 0 110-4 2 2 0 010 4zm3-9H9V6c0-1.66 1.34-3 3-3s3 1.34 3 3z"/></svg>',
  settings:
    '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M19.14 12.94c.04-.31.06-.63.06-.94 0-.31-.02-.63-.06-.94l2.03-1.58c.18-.14.23-.41.12-.61l-1.92-3.32c-.12-.22-.37-.29-.59-.22l-2.39.96c-.5-.38-1.03-.7-1.62-.94l-.36-2.54c-.04-.24-.24-.41-.48-.41h-3.84c-.24 0-.43.17-.47.41l-.36 2.54c-.59.24-1.13.57-1.62.94l-2.39-.96c-.22-.08-.47 0-.59.22L2.74 8.87c-.12.21-.08.47.12.61l2.03 1.58c-.04.31-.06.63-.06.94s.02.63.06.94l-2.03 1.58c-.18.14-.23.41-.12.61l1.92 3.32c.12.22.37.29.59.22l2.39-.96c.5.38 1.03.7 1.62.94l.36 2.54c.05.24.24.41.48.41h3.84c.24 0 .44-.17.47-.41l.36-2.54c.59-.24 1.13-.56 1.62-.94l2.39.96c.22.08.47 0 .59-.22l1.92-3.32c.12-.22.07-.47-.12-.61l-2.01-1.58zM12 15.6c-1.98 0-3.6-1.62-3.6-3.6s1.62-3.6 3.6-3.6 3.6 1.62 3.6 3.6-1.62 3.6-3.6 3.6z"/></svg>',
  menu: '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M3 18h18v-2H3v2zm0-5h18v-2H3v2zm0-7v2h18V6H3z"/></svg>',
  match:
    '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z"/></svg>',
  class:
    '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 3L1 9l11 6 9-4.91V17h2V9M5 13.18v4L12 21l7-3.82v-4L12 17l-7-3.82z"/></svg>'
};

/**
 * Resuelve el SVG de un ícono por nombre.
 *
 * Orden de resolución:
 *   1. Match literal en la variante pedida (los nombres bien escritos de
 *      Figma, p.ej. `comment` en `solid`, ganan siempre acá).
 *   2. Alias semántico/typo resuelto dentro de la variante pedida.
 *   3. La otra variante (literal o por alias) — con warn, porque el ícono
 *      pedido no existe en la variante solicitada.
 *   4. LEGACY_ICONS (literal o por alias) — sin warn de variante, es un
 *      ícono Material antiguo, no un fallback de Figma.
 *   5. `null` + warn si no se encuentra en ningún lado.
 */
export function getIcon(name: string, variant: IconVariant = 'linear'): string | null {
  const registry = ICON_REGISTRY[variant];
  const otherVariant: IconVariant = variant === 'linear' ? 'solid' : 'linear';
  const otherRegistry = ICON_REGISTRY[otherVariant];
  const aliasedName = ALIASES[name];

  if (registry[name]) {
    return registry[name];
  }
  if (aliasedName && registry[aliasedName]) {
    return registry[aliasedName];
  }

  if (otherRegistry[name]) {
    console.warn(
      `[ec-icon] "${name}" no existe en la variante "${variant}", usando "${otherVariant}" como fallback.`
    );
    return otherRegistry[name];
  }
  if (aliasedName && otherRegistry[aliasedName]) {
    console.warn(
      `[ec-icon] "${name}" no existe en la variante "${variant}", usando "${otherVariant}" como fallback.`
    );
    return otherRegistry[aliasedName];
  }

  const legacyName = aliasedName && LEGACY_ICONS[aliasedName] ? aliasedName : name;
  if (LEGACY_ICONS[legacyName]) {
    return LEGACY_ICONS[legacyName];
  }

  console.warn(`[ec-icon] ícono "${name}" no encontrado (variante "${variant}").`);
  return null;
}
